import Chevron from "@icons/chevron.svg?react";
import SearchIcon from "@icons/search-icon.svg?react";
import Tooltip from "@ui/tooltip/tooltip";
import clsx from "clsx";
import {
	type FunctionComponent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

import styles from "./base-input.module.scss";
import type { BaseInputProps, ExpandableOptions } from "./types";

type BaseInputComponentProps = BaseInputProps;

const DEFAULT_EXPANDABLE_OPTIONS: ExpandableOptions & {
	collapsedWidth: string;
	expandedWidth: string;
	collapseOnBlur: boolean;
	defaultExpanded: boolean;
} = {
	collapsedWidth: "48px",
	expandedWidth: "240px",
	collapseOnBlur: true,
	defaultExpanded: false,
};

type ResolvedExpandableOptions = typeof DEFAULT_EXPANDABLE_OPTIONS &
	ExpandableOptions;

const getExpandableOptions = (
	expandable: boolean | ExpandableOptions | undefined,
): ResolvedExpandableOptions | null => {
	if (!expandable) return null;
	if (expandable === true) return DEFAULT_EXPANDABLE_OPTIONS;
	return { ...DEFAULT_EXPANDABLE_OPTIONS, ...expandable };
};

export const BaseInput: FunctionComponent<BaseInputComponentProps> = ({
	label,
	error = false,
	errorText,
	helperText,
	size = "medium",
	variant = "outlined",
	fullWidth = false,
	startAdornment,
	endAdornment,
	labelBadge,
	containerClassName,
	labelClassName,
	inputClassName,
	errorClassName,
	id,
	disabled,
	required,
	className,
	ref,
	placeholder,
	min,
	max,
	step = 1,
	tooltipText,
	expandable,
	onBlur,
	onKeyDown,
	...restProps
}) => {
	const { t } = useTranslation();
	const internalRef = useRef<HTMLInputElement>(null);

	const inputRef = useCallback(
		(instance: HTMLInputElement | null) => {
			internalRef.current = instance;

			if (ref) {
				if (typeof ref === "function") {
					ref(instance);
				} else if ("current" in ref) {
					(ref as React.MutableRefObject<HTMLInputElement | null>).current =
						instance;
				}
			}
		},
		[ref],
	);

	const expandableOptions = getExpandableOptions(expandable);
	const [isExpanded, setIsExpanded] = useState(
		expandableOptions?.defaultExpanded ?? false,
	);

	const handleExpand = useCallback(() => {
		if (!expandableOptions || disabled) return;
		setIsExpanded(true);
		expandableOptions.onExpandChange?.(true);
		setTimeout(() => {
			internalRef.current?.focus();
		}, 50);
	}, [expandableOptions, disabled]);

	const handleCollapse = useCallback(() => {
		if (!expandableOptions?.collapseOnBlur) return;
		const value = restProps.value ?? internalRef.current?.value ?? "";
		if (!value) {
			setIsExpanded(false);
			expandableOptions.onExpandChange?.(false);
		}
	}, [expandableOptions, restProps.value]);

	const handleBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			handleCollapse();
			onBlur?.(e);
		},
		[handleCollapse, onBlur],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Escape" && expandableOptions) {
				setIsExpanded(false);
				expandableOptions.onExpandChange?.(false);
				internalRef.current?.blur();
			}
			onKeyDown?.(e);
		},
		[expandableOptions, onKeyDown],
	);

	useEffect(() => {
		if (expandableOptions?.defaultExpanded !== undefined) {
			setIsExpanded(expandableOptions.defaultExpanded);
		}
	}, [expandableOptions?.defaultExpanded]);

	const stepAmount = step === "any" || step === undefined ? 1 : Number(step);

	const handleIncrement = () => {
		if (restProps.type === "number" && restProps.onChange) {
			const newValue = Number(restProps.value || 0) + stepAmount;

			if (max === undefined || newValue <= Number(max)) {
				restProps.onChange({
					target: { value: newValue.toString() },
				} as React.ChangeEvent<HTMLInputElement>);
			}
		}
	};

	const handleDecrement = () => {
		if (restProps.type === "number" && restProps.onChange) {
			const newValue = Number(restProps.value || 0) - stepAmount;
			if (min === undefined || newValue >= Number(min)) {
				restProps.onChange({
					target: { value: newValue.toString() },
				} as React.ChangeEvent<HTMLInputElement>);
			}
		}
	};
	const numberInputOnWheelPreventChange = (
		e: React.WheelEvent<HTMLInputElement>,
	) => {
		if (e.currentTarget.type === "number") {
			(e.target as HTMLInputElement).blur();
		}
	};

	const containerClasses = clsx(
		styles.container,
		{
			[styles.fullWidth]: fullWidth && !expandableOptions,
			[styles.disabled]: disabled,
			[styles[`size_${size}`]]: size,
			[styles[`variant_${variant}`]]: variant,
			[styles.error]: error,
			[styles.expandable]: !!expandableOptions,
			[styles.expanded]: isExpanded,
		},
		containerClassName,
		expandableOptions ? className : undefined,
	);

	const inputClasses = clsx(
		styles.input,
		{
			[styles.hasStartAdornment]: !!startAdornment || !!expandableOptions,
			[styles.hasEndAdornment]: !!endAdornment,
			[styles.error]: error,
			[styles.expandableInput]: !!expandableOptions,
		},
		inputClassName,
		!expandableOptions ? className : undefined,
	);

	const labelClasses = clsx(
		styles.label,
		{
			[styles.required]: required,
			[styles.error]: error,
		},
		labelClassName,
	);

	const errorClasses = clsx(styles.errorText, errorClassName);

	const getHeightForSize = (inputSize: string) => {
		switch (inputSize) {
			case "small":
				return "36px";
			case "large":
				return "56px";
			default:
				return "48px";
		}
	};

	const expandableStyles = expandableOptions
		? ({
				"--expandable-collapsed-width": expandableOptions.collapsedWidth,
				"--expandable-expanded-width": expandableOptions.expandedWidth,
				"--expandable-height": getHeightForSize(size),
			} as React.CSSProperties)
		: undefined;

	const ExpandableIcon = expandableOptions?.icon ?? <SearchIcon />;

	return (
		<div className={containerClasses} style={expandableStyles}>
			{label && !expandableOptions && (
				<div className={styles.labelContainer}>
					<label htmlFor={id} className={labelClasses}>
						{label}
						{required && <span className={styles.requiredStar}>*</span>}
					</label>
					{tooltipText && <Tooltip position="bottom" title={tooltipText} />}
					{labelBadge}
				</div>
			)}

			<div className={styles.inputWrapper} role="presentation">
				{expandableOptions && (
					<button
						type="button"
						className={styles.expandableIconButton}
						onClick={handleExpand}
						disabled={disabled}
						aria-label={isExpanded ? "Search input" : "Expand search"}
						tabIndex={isExpanded ? -1 : 0}
					>
						{ExpandableIcon}
					</button>
				)}
				{startAdornment && !expandableOptions && (
					<div className={styles.startAdornment}>{startAdornment}</div>
				)}
				<input
					placeholder={t(placeholder ?? "")}
					ref={inputRef}
					id={id}
					disabled={disabled}
					required={required}
					min={min}
					max={max}
					step={step}
					className={inputClasses}
					autoComplete="off"
					onWheel={numberInputOnWheelPreventChange}
					onBlur={expandableOptions ? handleBlur : onBlur}
					onKeyDown={expandableOptions ? handleKeyDown : onKeyDown}
					{...restProps}
				/>

				{restProps.type === "number" && (
					<div className={styles.numberControls}>
						<button
							type="button"
							onClick={handleIncrement}
							className={styles.numberControl}
							disabled={
								disabled ||
								(max !== undefined && Number(restProps.value) >= Number(max))
							}
						>
							<Chevron style={{ rotate: "180deg" }} />
						</button>
						<button
							type="button"
							onClick={handleDecrement}
							className={styles.numberControl}
							disabled={
								disabled ||
								(min !== undefined && Number(restProps.value) <= Number(min))
							}
						>
							<Chevron />
						</button>
					</div>
				)}

				{endAdornment && (
					<div className={styles.endAdornment}>{endAdornment}</div>
				)}
			</div>

			{error && errorText && (
				<p id={`${id}-error`} className={errorClasses} role="alert">
					{t(errorText)}
				</p>
			)}

			{helperText && !error && (
				<p className={styles.helperText}>{helperText}</p>
			)}
		</div>
	);
};

BaseInput.displayName = "BaseInput";
